import { useTimeout } from '@mantine/hooks'
import { motion } from 'motion/react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import { UIButton } from 'src/components/ui/Button'
import { NewUserOnboardingContext } from './NewUserOnboardingContext'

export interface NewUserOnboardingStepperProps {
  overrideStepperContent?: ReactNode
}

function NewUserOnboardingStepper(props: NewUserOnboardingStepperProps) {
  const { overrideStepperContent } = props
  const { currentStep, totalStep } = useContext(NewUserOnboardingContext)
  const progressPercent = Math.min(1, (currentStep + 1) / totalStep)
  return (
    <motion.div
      layout
      layoutId="onboarding-stepper"
      transition={{ duration: 0 }}
      className="h-[52px] flex gap-2 justify-center items-center bg-neutral-100 dark:bg-neutral-700 border-b-[1px] border-neutral-200 dark:border-neutral-600"
    >
      {overrideStepperContent ? (
        overrideStepperContent
      ) : (
        <div className="bg-neutral-200 dark:bg-neutral-600 w-full h-1 rounded-full relative overflow-clip mx-6">
          <motion.div
            layout
            layoutId="onboarding-progress"
            className="bg-theme-primary h-full rounded-full absolute left-[-20%]"
            // motion does not work with zero size element, so we add a 20% hidden part
            style={{
              width: `${(progressPercent + 0.2) * 100}%`,
            }}
          />
        </div>
      )}
    </motion.div>
  )
}

export interface NewUserOnboardingPaginatorProps {
  disableNext?: boolean
  disallowBack?: boolean
  action?: () => Promise<void>
  overridePaginatorContent?: ReactNode
}

export function NewUserOnboardingSkipButton() {
  const [skippable, setSkippable] = useState(false)
  const { start: startSkipTimer } = useTimeout(() => setSkippable(true), 4000)
  const { skipStep, actionBusy } = useContext(NewUserOnboardingContext)
  useEffect(() => startSkipTimer(), [])
  const { t } = useTranslation(['common', 'user'])
  return (
    <UIButton
      disabled={!skippable || actionBusy}
      variant="ghost"
      className={twMerge(
        'float-right h-7 px-2 text-neutral-500 text-sm font-normal',
        skippable ? '' : 'opacity-0',
      )}
      onPress={skipStep}
    >
      {t('user:onboarding.progress.skip')}
    </UIButton>
  )
}

function NewUserOnboardingPaginator(props: NewUserOnboardingPaginatorProps) {
  const { disableNext, disallowBack, action, overridePaginatorContent } = props
  const {
    actionBusy,
    currentStep,
    totalStep,
    goToNextStepWithAction,
    backToLastStep,
  } = useContext(NewUserOnboardingContext)
  const { t } = useTranslation(['common', 'user'])
  return (
    <motion.div
      layout
      layoutId="onboarding-paginator"
      transition={{ duration: 0 }}
      className="h-16 flex gap-4 p-4 border-t-[1px] bg-white dark:bg-zinc-800 border-neutral-200 dark:border-neutral-600"
    >
      {overridePaginatorContent ? (
        overridePaginatorContent
      ) : (
        <>
          {currentStep > 0 && !disallowBack ? (
            <UIButton
              variant="ghost"
              className="px-2.5"
              disabled={actionBusy}
              onPress={backToLastStep}
            >
              {t('user:onboarding.progress.back')}
            </UIButton>
          ) : null}
          <div className="flex-1" />
          <UIButton
            className="w-24"
            color="theme"
            variant="filled"
            onPress={() => goToNextStepWithAction(action)}
            busy={actionBusy}
            disabled={disableNext}
          >
            {currentStep >= totalStep - 1
              ? t('user:onboarding.progress.finish')
              : t('user:onboarding.progress.next')}
          </UIButton>
        </>
      )}
    </motion.div>
  )
}

export function NewUserOnboardingFormStep(
  props: PropsWithChildren<
    NewUserOnboardingPaginatorProps & NewUserOnboardingStepperProps
  >,
) {
  const {
    disableNext,
    disallowBack,
    overrideStepperContent,
    action,
    overridePaginatorContent,
  } = props
  return (
    <motion.div
      layout
      layoutId="onboarding-step"
      className="absolute w-full h-full flex flex-col"
      transition={{ duration: 0 }}
    >
      <NewUserOnboardingStepper
        overrideStepperContent={overrideStepperContent}
      />
      <motion.div
        layoutScroll
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0, 1] }}
        exit={{ opacity: [1, 0, 0] }}
        transition={{ duration: 0.5 }}
        className="flex-1 p-6 overflow-auto contain-strict"
      >
        {props.children}
      </motion.div>
      <NewUserOnboardingPaginator
        disableNext={disableNext}
        disallowBack={disallowBack}
        action={action}
        overridePaginatorContent={overridePaginatorContent}
      />
    </motion.div>
  )
}
